import { useWsmWorkoutProfilesSource } from "./useWsmWorkoutProfilesSource";
import { useCloudWorkoutProfilesSource } from "./useCloudWorkoutProfilesSource";
import { PLATFORM_ENUM, WorkoutProfileType } from "@WahooFitness/cloud-client-types";
import { useConfigContext } from "@WahooFitness/wahoo-offline-mfe";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { KeyedMutator } from "swr";
import { DebouncedFunc } from "lodash";

export function sortWorkoutProfiles(a: WSMWorkoutProfile, b: WSMWorkoutProfile): number {
  const familySort =
    (a.workout_type_family_id ?? Number.POSITIVE_INFINITY) -
    (b.workout_type_family_id ?? Number.POSITIVE_INFINITY);
  return familySort !== 0 ? familySort : a.name.localeCompare(b.name);
}

export interface IWorkoutProfilesSource {
  workoutProfiles: WSMWorkoutProfile[] | undefined;
  getWorkoutProfile: (workoutProfileExternalId: string) => WSMWorkoutProfile | undefined;
  workoutProfilesError: Error | undefined;
  workoutProfilesAreLoading: boolean;
  workoutProfileUpdateInProgress: boolean;
  reloadWorkoutProfiles: KeyedMutator<WSMWorkoutProfile[]>;
  createWorkoutProfile: (
    workoutProfile: WorkoutProfileType
  ) => Promise<WSMWorkoutProfile | undefined | null>;
  editWorkoutProfile: (
    workoutProfileExternalId: string,
    edits: Partial<WorkoutProfileType>
  ) => Promise<WSMWorkoutProfile | undefined | null>;
  deleteWorkoutProfile: (workoutProfileExternalId: string) => Promise<boolean | null>;
  resetWorkoutProfile: (
    workoutProfileExternalId: string
  ) => Promise<WSMWorkoutProfile | undefined | null>;
  updateWorkoutProfilePagesImmediately: (
    workoutProfileExternalId: string,
    pages: Uint8Array
  ) => Promise<void | null>;
  updateWorkoutProfilePagesDebounce: DebouncedFunc<
    (workoutProfileExternalId: string, pages: Uint8Array) => Promise<void | null>
  >;
  getWorkoutProfilePages: (workoutProfileExternalId: string) => Promise<Uint8Array | undefined>;
  setIgnoreWorkoutProfileUpdates: (ignore: boolean) => void;
}

export const useWorkoutProfilesSource = (): IWorkoutProfilesSource => {
  const { platform } = useConfigContext();
  const offlineProfilesEnabled = platform !== PLATFORM_ENUM.web;

  const wsmProfilesSource = useWsmWorkoutProfilesSource(offlineProfilesEnabled);
  const cloudProfilesSource = useCloudWorkoutProfilesSource(!offlineProfilesEnabled);

  return offlineProfilesEnabled ? wsmProfilesSource : cloudProfilesSource;
};
